import jsonpath from 'jsonpath';
import ConfigureListField from 'components/HitchById/ConfigureField/ConfigureListField';
import ConfigureManualField from 'components/HitchById/ConfigureField/ConfigureManualField';
import ConfigureMappingField from 'components/HitchById/ConfigureField/ConfigureMappingField';
import ConfigureTableListField from 'components/HitchById/ConfigureField/ConfigureTableListField';
import ConfigureBooleanField from 'components/HitchById/ConfigureField/ConfigureBooleanField';
import ConfigureNumberField from 'components/HitchById/ConfigureField/ConfigureNumberField';
import capitalize from 'components/utils/capitalize';
import React from 'react';
import {
  BooleanChoice,
  Choice,
  ChoiceItem,
  JsonObject,
  ListChoice,
  ListChoiceItem,
  ManualChoice,
  ManualChoiceItem,
  MappingChoice,
  NumberChoice,
} from 'services/hitchit/types/Choices';
import {
  createFromList,
  createFromManualChoice,
  getLabelFromMapping,
} from 'services/hitchit/types/shared/mapChoice';

export type ChoicePanelProps = {
  jsonObject?: JsonObject;
  choiceId: string;
  choice: Choice<ChoiceItem>;
  preference?: unknown;
  savedPreference?: unknown;
  onChangeDealChoices: <CHOICE_TYPE>(choiceKey: string, choice: CHOICE_TYPE) => void;
  onReset: (choiceId: string) => void;
  onClear: (choiceId: string) => void;
  onCancel: (choiceId: string) => void;
  onSave: () => void;
  levelNested: number;
  prerequisiteChoicePanels: JSX.Element[];
  dependencyChoicePanels: JSX.Element[];
};

const createChoiceItemByType = (
  props: ChoicePanelProps
):
  | {
      title: string;
      configureField: JSX.Element;
    }
  | undefined => {
  const {
    jsonObject,
    choiceId,
    choice,
    preference,
    savedPreference,
    onChangeDealChoices,
    onReset,
    onClear,
    onCancel,
    onSave,
  } = props;
  switch (choice.type) {
    case 'list': {
      const listChoice = choice as ListChoice;
      /**
       * A list choice with 1 choice, where you cannot create a new one, and a default is chosen, should not be shown to the user
       * Except when the alwaysShow boolean is true
       */
      if (
        listChoice.default &&
        !listChoice.create &&
        listChoice.choices.length === 1 &&
        !listChoice.alwaysShow
      ) {
        return;
      }

      return {
        title: createFromList(preference as ListChoiceItem),
        configureField:
          listChoice.view?.type === 'table' ? (
            <ConfigureTableListField
              choice={listChoice}
              preference={preference as ListChoiceItem}
              savedPreference={savedPreference as ListChoiceItem}
              onChange={(choice) =>
                onChangeDealChoices<ListChoiceItem | undefined>(choiceId, choice)
              }
              onReset={() => onReset(choiceId)}
              onClear={() => onClear(choiceId)}
              onCancel={() => onCancel(choiceId)}
              onSave={() => onSave()}
            />
          ) : (
            <ConfigureListField
              choice={listChoice}
              preference={preference as ListChoiceItem}
              savedPreference={savedPreference as ListChoiceItem}
              onChange={(choice) =>
                onChangeDealChoices<ListChoiceItem | undefined>(choiceId, choice)
              }
              onReset={() => onReset(choiceId)}
              onClear={() => onClear(choiceId)}
              onCancel={() => onCancel(choiceId)}
              onSave={() => onSave()}
            />
          ),
      };
    }
    case 'manual': {
      const manualChoice = choice as ManualChoice;

      let jsonObjectFromField = jsonObject;
      // This is done when the order field is not used for selection purposes, but some level deeper
      if (manualChoice.fromField) {
        jsonObjectFromField = jsonpath.query(
          jsonObject,
          manualChoice.fromField
        ) as unknown as JsonObject;
      }

      const jsonObjectForManualChoice =
        Array.isArray(jsonObjectFromField) && jsonObjectFromField.length > 0
          ? jsonObjectFromField[0]
          : jsonObjectFromField;
      const onlyPrefix = manualChoice.onlyPrefix;

      return {
        title: createFromManualChoice(
          preference as ManualChoiceItem[],
          jsonObjectForManualChoice,
          onlyPrefix
        ),
        configureField: (
          <ConfigureManualField
            jsonObject={jsonObjectForManualChoice}
            manualChoice={manualChoice}
            preference={preference as ManualChoiceItem[]}
            savedPreference={savedPreference as ManualChoiceItem[]}
            onChange={(choice) => onChangeDealChoices<ManualChoiceItem[]>(choiceId, choice)}
            onReset={() => onReset(choiceId)}
            onClear={() => onClear(choiceId)}
            onCancel={() => onCancel(choiceId)}
            onSave={() => onSave()}
          />
        ),
      };
    }
    case 'mapping': {
      const mappingChoice = choice as MappingChoice;
      return {
        title: capitalize(
          getLabelFromMapping(mappingChoice, preference as Record<string, string>, jsonObject)
        ),
        configureField: (
          <ConfigureMappingField
            choice={mappingChoice}
            preference={preference as Record<string, string>}
            savedPreference={savedPreference as Record<string, string>}
            onChange={(choice) =>
              onChangeDealChoices<Record<string, string> | undefined>(choiceId, choice)
            }
            onReset={() => onReset(choiceId)}
            onClear={() => onClear(choiceId)}
            onCancel={() => onCancel(choiceId)}
            onSave={() => onSave()}
          />
        ),
      };
    }
    case 'boolean': {
      const booleanChoice = choice as BooleanChoice;
      return {
        title: capitalize(typeof preference !== 'undefined' ? `${preference ? 'Yes' : 'No'}` : ''),
        configureField: (
          <ConfigureBooleanField
            choice={booleanChoice}
            preference={preference as boolean}
            savedPreference={savedPreference as boolean}
            onChange={(choice) => onChangeDealChoices<boolean | undefined>(choiceId, choice)}
            onReset={() => onReset(choiceId)}
            onCancel={() => onCancel(choiceId)}
            onSave={() => onSave()}
          />
        ),
      };
    }
    case 'number': {
      const numberChoice = choice as NumberChoice;

      return {
        title: capitalize(preference ? (preference as number).toString() : ''),
        configureField: (
          <ConfigureNumberField
            choice={numberChoice}
            preference={preference as number}
            savedPreference={savedPreference as number}
            onChange={(choice) => onChangeDealChoices<number | undefined>(choiceId, choice)}
            onReset={() => onReset(choiceId)}
            onClear={() => onClear(choiceId)}
            onCancel={() => onCancel(choiceId)}
            onSave={() => onSave()}
          />
        ),
      };
    }

    default:
      throw new Error(`unsupported type ${(choice as any).type}`);
  }
};

export default createChoiceItemByType;
